/** /api_requests/ — ops-wide inbound webhook / API call log search.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). The `filters` param is the QuerySearch widget's wire
 * AST, URL-encoded as a JSON array — identical contract to /import_runs/, but
 * over API_Request (see ApiRequestFilterNode in the OpenAPI spec). Rows have
 * integer ids.
 *
 * The table is very large and unpartitioned: every search namespace is an
 * EXACT match against an indexed column, and payload columns (headers, body,
 * response, data) are never inlined in list rows — the detail endpoint
 * carries headers/data/response, the body endpoint serves the (possibly
 * S3-stored) request body.
 */
import { apiGet, apiPost, extendedListConfig } from './client';
import type { CountMode, CountOnlyResult } from './countStatus';
import type { PaginatedResponse } from './users';
import type { WireNode } from '@/components/QuerySearch';
import type { AiDiagStartBody, DiagnoseLaunchContext } from '@/features/ai-diag/types';
import type { TenantIdsParam } from '@/scope/tenantIds';
import type { TourOperatorEnvironment } from './tourOperators';

export interface ApiRequestListItem {
  id: number;
  /** api_request_id UUID. */
  uuid: string;
  created_ts: string;
  updated_ts: string | null;
  /** Owning tenant; null for tenant-less requests. `environment`: the Tenant cell marks a
   *  sandbox. */
  tenant: { id: number; name: string; environment?: TourOperatorEnvironment | null } | null;
  name: string | null;
  url: string | null;
  method: string | null;
  content_type: string | null;
  view: string | null;
  ip: string | null;
  /** Response HTTP status (0 = not recorded). */
  status_code: number | null;
  duration_ms: number | null;
  /** Integration codename; empty for non-integration requests (reimport /
   *  flush queues unavailable there). */
  integration: string | null;
  /** API_Request.TYPES — see API_REQUEST_TYPE_LABELS (0-based). */
  type: number | null;
  request_id: string | null;
  booking_id: string | null;
  booking_contact: string | null;
  booking_status: string | null;
  booking_product: string | null;
  booking_datetime: string | null;
  booking_tickets: string | null;
  /** Set when a webhook (re)import failed; cleared by a successful reimport. */
  error_series_id: string | null;
  error: string | null;
  /** API_Request.STORE_OPTIONS — see API_REQUEST_STORE_LABELS (0-based). */
  store: number | null;
  expires_ts: string | null;
  /** True when the body is offloaded to S3. */
  has_body_file: boolean;
}

export interface ApiRequestLinkedBooking {
  id: number;
  email: string | null;
  tour: string | null;
  date: string | null;
  tenant_id: number | null;
}

export interface ApiRequestLinkedOrder {
  id: number;
  order_ref: string | null;
  tenant_id: number | null;
}

export interface ApiRequestDetail extends ApiRequestListItem {
  headers: Record<string, unknown> | null;
  data: Record<string, unknown> | null;
  response: string | null;
  response_headers: Record<string, unknown> | null;
  response_content_type: string | null;
  bookings: ApiRequestLinkedBooking[];
  orders: ApiRequestLinkedOrder[];
}

export type ApiRequestListResponse = PaginatedResponse<ApiRequestListItem> & {
  /** Present in `count_mode=sync` only: the sync count is capped at 10 000
   *  probed rows. The async/deferred on-demand total is exact and uncapped, so
   *  there is nothing to cap and the key is absent. */
  count_is_capped?: boolean;
};

export interface ApiRequestListParams {
  /** Extended statement-budget tier for this search (audited server-side). */
  timeout?: 'extended';
  page?: number;
  page_size?: number;
  ordering?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  filters?: WireNode[];
  /** Override the endpoint's own row-total mode. Omit to use the server
   *  default (`deferred` on this grid) — that omission is what keeps a cold
   *  first load free of a COUNT. */
  count_mode?: CountMode;
  /** Ask only for the total; no page, no rows. */
  count_only?: boolean;
}

function toQuery(p: ApiRequestListParams): string {
  const usp = new URLSearchParams();
  if (p.page) usp.set('page', String(p.page));
  if (p.page_size) usp.set('page_size', String(p.page_size));
  if (p.ordering) usp.set('ordering', p.ordering);
  if (p.timeout) usp.set('timeout', p.timeout);
  if (p.tour_operator_id !== undefined) usp.set('tour_operator_id', String(p.tour_operator_id));
  // Omit `filters` entirely when empty (never send `filters=[]`).
  if (p.filters && p.filters.length > 0) usp.set('filters', JSON.stringify(p.filters));
  if (p.count_mode) usp.set('count_mode', p.count_mode);
  if (p.count_only) usp.set('count_only', '1');
  return usp.toString();
}

export async function list(p: ApiRequestListParams = {}): Promise<ApiRequestListResponse> {
  const qs = toQuery(p);
  return apiGet(`/api_requests/${qs ? `?${qs}` : ''}`, extendedListConfig(p.timeout));
}

/** Total only, for the grid's on-demand row count. `async` so the count runs
 *  in Celery: an exact, uncapped total that never occupies a web worker. */
export async function count(p: ApiRequestListParams = {}): Promise<CountOnlyResult> {
  const qs = toQuery({ ...p, count_only: true, count_mode: 'async' });
  return apiGet(`/api_requests/?${qs}`);
}

/** GET /api_requests/{id}/ — full request info + touched bookings/orders. */
export async function retrieve(id: number | string): Promise<ApiRequestDetail> {
  return apiGet(`/api_requests/${id}/`);
}

/** GET /api_requests/{id}/body/ — the raw request body as text (fetched from
 *  S3 when offloaded), with a sniffed format for the highlighter. */
export async function body(
  id: number | string,
): Promise<{ body: string | null; format: 'json' | 'xml' | 'text' }> {
  return apiGet(`/api_requests/${id}/body/`);
}

/** Reimport params (the `apireq reimport` management-command semantics). */
export interface ReimportParams {
  /** 'default' keeps the integration's own notification config. */
  send_notifications: 'default' | 'send' | 'skip';
  /** Also reimport requests whose bookings are all in the past. */
  include_past: boolean;
  /** Skip a request when a newer request exists for the same booking id. */
  ignore_if_future_updates: boolean;
  force: boolean;
}

// ---- AI diagnostics (perm: console_import_ai) -------------------------------
// Same tool as the import-runs Diagnose tab; this is the webhook-anchored
// entry point (covers "my webhook never became an import").

export async function diagnoseLaunchContext(): Promise<DiagnoseLaunchContext> {
  return apiGet('/api_requests/diagnose/launch_context/');
}

/** POST /api_requests/{id}/diagnose/ — start an AI diagnostic run (or a
 *  follow-up when `thread_task_id` is set). 202 { task_id } — poll bgtasks;
 *  409 carries the already-running task_id (adopt it). */
export async function startDiagnose(
  id: number | string,
  body: AiDiagStartBody,
): Promise<{ task_id: string; queued: boolean }> {
  return apiPost(`/api_requests/${id}/diagnose/`, body);
}

// Tenant suggestions are the SAME shared Bookings autocomplete endpoint;
// re-export the fetcher factory so apiRequestNamespaces has one import.
export { makeSuggestionFetcher } from './bookings';
